import React, { memo } from 'react'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Skeleton } from '@mui/material'
import { WaterlineCalendarRow } from '../views/corp/waterline-management/types'
import { BioCalendarRow } from '../views/corp/biological-monitoring/types'
import { BorderLessTableCell, StyledPracticeCell } from './Table.styled'

type CorpCalendarTableProps = {
  practices: WaterlineCalendarRow[] | BioCalendarRow[] | null,
  isLoading: boolean,
  testResultsCell: React.ComponentType<{ practice: WaterlineCalendarRow | BioCalendarRow }>
  practiceLink: React.ComponentType<{ practice: BioCalendarRow | WaterlineCalendarRow }>,
  yearHeader?: React.ReactNode,
  quarterHeader?: React.ReactNode
  monthHeader?: React.FunctionComponentElement<{ fromDate: string, toDate: string }>
}

const CorpCalendarTable = (props: CorpCalendarTableProps) => {
  const {
    practices,
    isLoading,
    testResultsCell,
    yearHeader,
    quarterHeader,
    monthHeader,
    practiceLink
  } = props

  return (
    <TableContainer component={Paper}>
      <Table aria-label="waterline management calendar">
        <TableHead>
          <TableRow>
            <BorderLessTableCell sx={{ minWidth: 300 }}></BorderLessTableCell>
            <BorderLessTableCell sx={{ minWidth: 250 }}></BorderLessTableCell>
            <BorderLessTableCell sx={{ minWidth: 150 }}></BorderLessTableCell>
            <BorderLessTableCell sx={{ minWidth: 150 }}></BorderLessTableCell>
            {yearHeader ?? null}
          </TableRow>
          <TableRow sx={{ '& td, & th': { paddingBottom: '8px' } }}>
            <StyledPracticeCell sx={{ minWidth: 400 }}>Name</StyledPracticeCell>
            <StyledPracticeCell sx={{ minWidth: 150 }}>City</StyledPracticeCell>
            <StyledPracticeCell sx={{ minWidth: 100 }}>Customer #</StyledPracticeCell>
            <StyledPracticeCell sx={{ minWidth: 150 }}>Consistency %</StyledPracticeCell>
            {quarterHeader ?? null}
            {monthHeader ?? null}
          </TableRow>
        </TableHead>
        <TableBody>
          {
            !isLoading ? practices?.map((practice: WaterlineCalendarRow | BioCalendarRow) => (
              <TableRow
                key={`${practice.practiceName}-${practice.customerNumber}-row`}
                sx={{ '&:last-child td, &:last-child th': { border: 0 }, '& td, & th': { textTransform: 'uppercase' } }}
                className="waterline-calendar-practice"
              >
                <StyledPracticeCell component="th" scope="row" sx={{ minWidth: 300 }}>
                  {React.createElement(practiceLink, { practice })}
                </StyledPracticeCell>
                <StyledPracticeCell sx={{ minWidth: 150 }}>{`(${practice.practiceState}) ${practice.practiceCity}`}</StyledPracticeCell>
                <StyledPracticeCell sx={{ minWidth: 100 }}>{practice.customerNumber}</StyledPracticeCell>
                <StyledPracticeCell sx={{ minWidth: 150 }}>{`${practice.compliance.totalStatus}%`}</StyledPracticeCell>
                {React.createElement(testResultsCell, { practice })}
              </TableRow>
            )) : (
              [...Array(10)].map((_, rowKey) => {
                return (
                  <TableRow key={rowKey}>
                    {[...Array(5)].map((_, cellKey) => {
                      const minWidthMap = new Map([[0, 300], [1, 250], [2, 150], [3, 150]])
                      return (
                        <TableCell sx={{ minWidth: minWidthMap.get(cellKey) }} key={cellKey}>
                          <Skeleton />
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )
              })
            )
          }
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default memo(CorpCalendarTable)
